import type { SubscriptionTierResponse } from '../../types/generated/models/subscription-tier-response'
import type { TierFeatureResponse } from '../../types/generated/models/tier-feature-response'
import type { TierPricingResponse } from '../../types/generated/models/tier-pricing-response'
import type { CheckoutCountryResponse } from '../../types/generated/models/checkout-country-response'

/* ── State interface ── */

export interface TermsAndConditionsVersion {
  id: number
  languageCode: string
  version: string
  title: string
  content?: string
  contentLength?: number
  status: 'Draft' | 'Published'
  publishedAt: string | null
  publishedByName: string | null
  createdAt: string | null
  createdByName: string | null
  modifiedAt: string | null
  modifiedByName: string | null
}

export interface SubscriptionManagementState {
  tiers: SubscriptionTierResponse[]
  tierFeatures: TierFeatureResponse[]
  tierPricing: TierPricingResponse[]
  countries: CheckoutCountryResponse[]
  termsVersions: TermsAndConditionsVersion[]
  selectedTierId: number | null
  activeTab: 'tiers' | 'countries' | 'terms'
  loading: boolean
  saving: boolean
  error: boolean | null
  errorMessage: string | null
  successMessage: string | null
}

// Subscription Management - Admin CRUD for tiers, features, pricing, countries, currencies

// --- Tiers ---
export const GET_SUBSCRIPTION_TIERS = 'GET_SUBSCRIPTION_TIERS'
export const GET_SUBSCRIPTION_TIERS_SUCCESS = 'GET_SUBSCRIPTION_TIERS_SUCCESS'
export const GET_SUBSCRIPTION_TIERS_FAIL = 'GET_SUBSCRIPTION_TIERS_FAIL'

export const CREATE_SUBSCRIPTION_TIER = 'CREATE_SUBSCRIPTION_TIER'
export const CREATE_SUBSCRIPTION_TIER_SUCCESS = 'CREATE_SUBSCRIPTION_TIER_SUCCESS'
export const CREATE_SUBSCRIPTION_TIER_FAIL = 'CREATE_SUBSCRIPTION_TIER_FAIL'

export const UPDATE_SUBSCRIPTION_TIER = 'UPDATE_SUBSCRIPTION_TIER'
export const UPDATE_SUBSCRIPTION_TIER_SUCCESS = 'UPDATE_SUBSCRIPTION_TIER_SUCCESS'
export const UPDATE_SUBSCRIPTION_TIER_FAIL = 'UPDATE_SUBSCRIPTION_TIER_FAIL'

export const DELETE_SUBSCRIPTION_TIER = 'DELETE_SUBSCRIPTION_TIER'
export const DELETE_SUBSCRIPTION_TIER_SUCCESS = 'DELETE_SUBSCRIPTION_TIER_SUCCESS'
export const DELETE_SUBSCRIPTION_TIER_FAIL = 'DELETE_SUBSCRIPTION_TIER_FAIL'

// --- Tier Features ---
export const GET_TIER_FEATURES = 'GET_TIER_FEATURES'
export const GET_TIER_FEATURES_SUCCESS = 'GET_TIER_FEATURES_SUCCESS'
export const GET_TIER_FEATURES_FAIL = 'GET_TIER_FEATURES_FAIL'

export const UPDATE_TIER_FEATURES = 'UPDATE_TIER_FEATURES'
export const UPDATE_TIER_FEATURES_SUCCESS = 'UPDATE_TIER_FEATURES_SUCCESS'
export const UPDATE_TIER_FEATURES_FAIL = 'UPDATE_TIER_FEATURES_FAIL'

// --- Tier Pricing ---
export const GET_TIER_PRICING = 'GET_TIER_PRICING'
export const GET_TIER_PRICING_SUCCESS = 'GET_TIER_PRICING_SUCCESS'
export const GET_TIER_PRICING_FAIL = 'GET_TIER_PRICING_FAIL'

export const UPDATE_TIER_PRICING = 'UPDATE_TIER_PRICING'
export const UPDATE_TIER_PRICING_SUCCESS = 'UPDATE_TIER_PRICING_SUCCESS'
export const UPDATE_TIER_PRICING_FAIL = 'UPDATE_TIER_PRICING_FAIL'

// --- Countries ---
export const GET_CHECKOUT_COUNTRIES = 'GET_CHECKOUT_COUNTRIES'
export const GET_CHECKOUT_COUNTRIES_SUCCESS = 'GET_CHECKOUT_COUNTRIES_SUCCESS'
export const GET_CHECKOUT_COUNTRIES_FAIL = 'GET_CHECKOUT_COUNTRIES_FAIL'

export const CREATE_CHECKOUT_COUNTRY = 'CREATE_CHECKOUT_COUNTRY'
export const CREATE_CHECKOUT_COUNTRY_SUCCESS = 'CREATE_CHECKOUT_COUNTRY_SUCCESS'
export const CREATE_CHECKOUT_COUNTRY_FAIL = 'CREATE_CHECKOUT_COUNTRY_FAIL'

export const UPDATE_CHECKOUT_COUNTRY = 'UPDATE_CHECKOUT_COUNTRY'
export const UPDATE_CHECKOUT_COUNTRY_SUCCESS = 'UPDATE_CHECKOUT_COUNTRY_SUCCESS'
export const UPDATE_CHECKOUT_COUNTRY_FAIL = 'UPDATE_CHECKOUT_COUNTRY_FAIL'

export const DELETE_CHECKOUT_COUNTRY = 'DELETE_CHECKOUT_COUNTRY'
export const DELETE_CHECKOUT_COUNTRY_SUCCESS = 'DELETE_CHECKOUT_COUNTRY_SUCCESS'
export const DELETE_CHECKOUT_COUNTRY_FAIL = 'DELETE_CHECKOUT_COUNTRY_FAIL'

// --- Country Currencies ---
export const UPDATE_COUNTRY_CURRENCIES = 'UPDATE_COUNTRY_CURRENCIES'
export const UPDATE_COUNTRY_CURRENCIES_SUCCESS = 'UPDATE_COUNTRY_CURRENCIES_SUCCESS'
export const UPDATE_COUNTRY_CURRENCIES_FAIL = 'UPDATE_COUNTRY_CURRENCIES_FAIL'

// --- Terms & Conditions ---
export const GET_TERMS_VERSIONS = 'GET_TERMS_VERSIONS'
export const GET_TERMS_VERSIONS_SUCCESS = 'GET_TERMS_VERSIONS_SUCCESS'
export const GET_TERMS_VERSIONS_FAIL = 'GET_TERMS_VERSIONS_FAIL'

export const CREATE_TERMS_VERSION = 'CREATE_TERMS_VERSION'
export const CREATE_TERMS_VERSION_SUCCESS = 'CREATE_TERMS_VERSION_SUCCESS'
export const CREATE_TERMS_VERSION_FAIL = 'CREATE_TERMS_VERSION_FAIL'

export const UPDATE_TERMS_VERSION = 'UPDATE_TERMS_VERSION'
export const UPDATE_TERMS_VERSION_SUCCESS = 'UPDATE_TERMS_VERSION_SUCCESS'
export const UPDATE_TERMS_VERSION_FAIL = 'UPDATE_TERMS_VERSION_FAIL'

export const PUBLISH_TERMS_VERSION = 'PUBLISH_TERMS_VERSION'
export const PUBLISH_TERMS_VERSION_SUCCESS = 'PUBLISH_TERMS_VERSION_SUCCESS'
export const PUBLISH_TERMS_VERSION_FAIL = 'PUBLISH_TERMS_VERSION_FAIL'

export const DELETE_TERMS_VERSION = 'DELETE_TERMS_VERSION'
export const DELETE_TERMS_VERSION_SUCCESS = 'DELETE_TERMS_VERSION_SUCCESS'
export const DELETE_TERMS_VERSION_FAIL = 'DELETE_TERMS_VERSION_FAIL'

export const GET_TERMS_VERSION = 'GET_TERMS_VERSION'
export const GET_TERMS_VERSION_SUCCESS = 'GET_TERMS_VERSION_SUCCESS'
export const GET_TERMS_VERSION_FAIL = 'GET_TERMS_VERSION_FAIL'

// --- Local state ---
export const SET_ACTIVE_TAB = 'SUBMGMT_SET_ACTIVE_TAB'
export const SET_SELECTED_TIER = 'SUBMGMT_SET_SELECTED_TIER'
export const CLEAR_SUBMGMT_MESSAGES = 'CLEAR_SUBMGMT_MESSAGES'

export const defaultSubscriptionManagementState: SubscriptionManagementState = {
  tiers: [],
  tierFeatures: [],
  tierPricing: [],
  countries: [],
  termsVersions: [],
  selectedTierId: null,
  activeTab: 'tiers', // 'tiers' | 'countries' | 'terms'
  loading: false,
  saving: false,
  error: false,
  errorMessage: null,
  successMessage: null
}

export default (state: SubscriptionManagementState = defaultSubscriptionManagementState, action: any): SubscriptionManagementState => {
  switch (action.type) {
    // --- Tiers ---
    case GET_SUBSCRIPTION_TIERS:
      return { ...state, loading: true, error: false, errorMessage: null }
    case GET_SUBSCRIPTION_TIERS_SUCCESS:
      return {
        ...state,
        tiers: action.payload.data?.data || action.payload.data || [],
        loading: false,
        error: false
      }
    case GET_SUBSCRIPTION_TIERS_FAIL:
      return { ...state, loading: false, error: true, errorMessage: 'Failed to load subscription tiers' }

    case CREATE_SUBSCRIPTION_TIER:
      return { ...state, saving: true, error: false, errorMessage: null, successMessage: null }
    case CREATE_SUBSCRIPTION_TIER_SUCCESS:
      return { ...state, saving: false, successMessage: 'Tier created successfully' }
    case CREATE_SUBSCRIPTION_TIER_FAIL:
      return { ...state, saving: false, error: true, errorMessage: 'Failed to create tier' }

    case UPDATE_SUBSCRIPTION_TIER:
      return { ...state, saving: true, error: false, errorMessage: null, successMessage: null }
    case UPDATE_SUBSCRIPTION_TIER_SUCCESS:
      return { ...state, saving: false, successMessage: 'Tier updated successfully' }
    case UPDATE_SUBSCRIPTION_TIER_FAIL:
      return { ...state, saving: false, error: true, errorMessage: 'Failed to update tier' }

    case DELETE_SUBSCRIPTION_TIER:
      return { ...state, saving: true, error: false, errorMessage: null, successMessage: null }
    case DELETE_SUBSCRIPTION_TIER_SUCCESS:
      return { ...state, saving: false, successMessage: 'Tier deleted successfully' }
    case DELETE_SUBSCRIPTION_TIER_FAIL:
      return { ...state, saving: false, error: true, errorMessage: 'Failed to delete tier' }

    // --- Tier Features ---
    case GET_TIER_FEATURES:
      return { ...state, loading: true, error: false, errorMessage: null }
    case GET_TIER_FEATURES_SUCCESS:
      return {
        ...state,
        tierFeatures: action.payload.data?.data || action.payload.data || [],
        loading: false,
        error: false
      }
    case GET_TIER_FEATURES_FAIL:
      return { ...state, loading: false, error: true, errorMessage: 'Failed to load tier features' }

    case UPDATE_TIER_FEATURES:
      return { ...state, saving: true, error: false, errorMessage: null, successMessage: null }
    case UPDATE_TIER_FEATURES_SUCCESS:
      return { ...state, saving: false, successMessage: 'Tier features updated successfully' }
    case UPDATE_TIER_FEATURES_FAIL:
      return { ...state, saving: false, error: true, errorMessage: 'Failed to update tier features' }

    // --- Tier Pricing ---
    case GET_TIER_PRICING:
      return { ...state, loading: true, error: false, errorMessage: null }
    case GET_TIER_PRICING_SUCCESS:
      return {
        ...state,
        tierPricing: action.payload.data?.data || action.payload.data || [],
        loading: false,
        error: false
      }
    case GET_TIER_PRICING_FAIL:
      return { ...state, loading: false, error: true, errorMessage: 'Failed to load tier pricing' }

    case UPDATE_TIER_PRICING:
      return { ...state, saving: true, error: false, errorMessage: null, successMessage: null }
    case UPDATE_TIER_PRICING_SUCCESS:
      return { ...state, saving: false, successMessage: 'Tier pricing updated successfully' }
    case UPDATE_TIER_PRICING_FAIL:
      return { ...state, saving: false, error: true, errorMessage: 'Failed to update tier pricing' }

    // --- Countries ---
    case GET_CHECKOUT_COUNTRIES:
      return { ...state, loading: true, error: false, errorMessage: null }
    case GET_CHECKOUT_COUNTRIES_SUCCESS:
      return {
        ...state,
        countries: action.payload.data?.data || action.payload.data || [],
        loading: false,
        error: false
      }
    case GET_CHECKOUT_COUNTRIES_FAIL:
      return { ...state, loading: false, error: true, errorMessage: 'Failed to load countries' }

    case CREATE_CHECKOUT_COUNTRY:
      return { ...state, saving: true, error: false, errorMessage: null, successMessage: null }
    case CREATE_CHECKOUT_COUNTRY_SUCCESS:
      return { ...state, saving: false, successMessage: 'Country created successfully' }
    case CREATE_CHECKOUT_COUNTRY_FAIL:
      return { ...state, saving: false, error: true, errorMessage: 'Failed to create country' }

    case UPDATE_CHECKOUT_COUNTRY:
      return { ...state, saving: true, error: false, errorMessage: null, successMessage: null }
    case UPDATE_CHECKOUT_COUNTRY_SUCCESS:
      return { ...state, saving: false, successMessage: 'Country updated successfully' }
    case UPDATE_CHECKOUT_COUNTRY_FAIL:
      return { ...state, saving: false, error: true, errorMessage: 'Failed to update country' }

    case DELETE_CHECKOUT_COUNTRY:
      return { ...state, saving: true, error: false, errorMessage: null, successMessage: null }
    case DELETE_CHECKOUT_COUNTRY_SUCCESS:
      return { ...state, saving: false, successMessage: 'Country deleted successfully' }
    case DELETE_CHECKOUT_COUNTRY_FAIL:
      return { ...state, saving: false, error: true, errorMessage: 'Failed to delete country' }

    // --- Country Currencies ---
    case UPDATE_COUNTRY_CURRENCIES:
      return { ...state, saving: true, error: false, errorMessage: null, successMessage: null }
    case UPDATE_COUNTRY_CURRENCIES_SUCCESS:
      return { ...state, saving: false, successMessage: 'Country currencies updated successfully' }
    case UPDATE_COUNTRY_CURRENCIES_FAIL:
      return { ...state, saving: false, error: true, errorMessage: 'Failed to update country currencies' }

    // --- Terms & Conditions ---
    case GET_TERMS_VERSIONS:
      return { ...state, loading: true, error: false, errorMessage: null }
    case GET_TERMS_VERSIONS_SUCCESS:
      return {
        ...state,
        termsVersions: action.payload.data?.data || action.payload.data || [],
        loading: false,
        error: false
      }
    case GET_TERMS_VERSIONS_FAIL:
      return { ...state, loading: false, error: true, errorMessage: 'Failed to load terms and conditions' }

    case CREATE_TERMS_VERSION:
      return { ...state, saving: true, error: false, errorMessage: null, successMessage: null }
    case CREATE_TERMS_VERSION_SUCCESS:
      return { ...state, saving: false, successMessage: 'Terms version created successfully' }
    case CREATE_TERMS_VERSION_FAIL:
      return { ...state, saving: false, error: true, errorMessage: 'Failed to create terms version' }

    case UPDATE_TERMS_VERSION:
      return { ...state, saving: true, error: false, errorMessage: null, successMessage: null }
    case UPDATE_TERMS_VERSION_SUCCESS:
      return { ...state, saving: false, successMessage: 'Terms version updated successfully' }
    case UPDATE_TERMS_VERSION_FAIL:
      return { ...state, saving: false, error: true, errorMessage: 'Failed to update terms version' }

    case PUBLISH_TERMS_VERSION:
      return { ...state, saving: true, error: false, errorMessage: null, successMessage: null }
    case PUBLISH_TERMS_VERSION_SUCCESS:
      return { ...state, saving: false, successMessage: 'Terms version published successfully' }
    case PUBLISH_TERMS_VERSION_FAIL:
      return { ...state, saving: false, error: true, errorMessage: 'Failed to publish terms version' }

    case DELETE_TERMS_VERSION:
      return { ...state, saving: true, error: false, errorMessage: null, successMessage: null }
    case DELETE_TERMS_VERSION_SUCCESS:
      return { ...state, saving: false, successMessage: 'Terms version deleted successfully' }
    case DELETE_TERMS_VERSION_FAIL:
      return { ...state, saving: false, error: true, errorMessage: 'Failed to delete terms version' }

    case GET_TERMS_VERSION:
      return { ...state, loading: true, error: false, errorMessage: null }
    case GET_TERMS_VERSION_SUCCESS:
      return {
        ...state,
        termsVersions: state.termsVersions.map(t =>
          (t.id === action.payload.data?.id || t.id === action.payload.data?.data?.id)
            ? { ...t, ...action.payload.data?.data, ...action.payload.data }
            : t
        ),
        loading: false
      }
    case GET_TERMS_VERSION_FAIL:
      return { ...state, loading: false, error: true, errorMessage: 'Failed to load terms version' }

    // --- Local state ---
    case SET_ACTIVE_TAB:
      return { ...state, activeTab: action.payload }
    case SET_SELECTED_TIER:
      return { ...state, selectedTierId: action.payload }
    case CLEAR_SUBMGMT_MESSAGES:
      return { ...state, error: false, errorMessage: null, successMessage: null }

    default:
      return state
  }
}
